import React, { useContext, useEffect } from 'react';
import { Icon, Panel, RadioCardGroup } from '@printdeal/ui-library';
import { useExperiment } from '@printdeal/feature-management/react';
import clsx from 'clsx';
import { useSelectorMachineSend, VatModeContext } from '@printdeal/product-selector';
import { FormattedMessage } from 'react-intl';
import { Money } from '../../../../Money';
import { dispatchDataLayerEntry } from '../../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import type { Service } from '../../../../../types/product/productDetailPage';
import type { SelectedDeliveryTool } from '../../../../../types/file-delivery/FileDelivery';

interface Props {
  services: Service[];
  alternativeUploadService: string | undefined;
  setSelectedServices: React.Dispatch<React.SetStateAction<string[]>>;
  setAlternativeUploadService: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedDeliveryTool: React.Dispatch<React.SetStateAction<SelectedDeliveryTool | undefined>>;
}

export const AlternativeUploadFileCheck = ({
  services, alternativeUploadService, setSelectedServices, setAlternativeUploadService, setSelectedDeliveryTool,
}: Props) => {
  const send = useSelectorMachineSend();
  const { vatMode } = useContext(VatModeContext);
  const exp1692 = useExperiment('exp1692_alternative_upload_file_check_pdp_v3');
  const fileCheckService = services.find((service) => service.name === 'File Check');

  useEffect(() => {
    // Set editor type to direct-upload if no service is selected
    if (alternativeUploadService === 'no-service') {
      setSelectedDeliveryTool('direct-upload');
    } else {
      setSelectedDeliveryTool(undefined);
    }
  }, [alternativeUploadService, setSelectedDeliveryTool]);

  if (exp1692.experiment.variation !== 'variant' || !fileCheckService) {
    setAlternativeUploadService('no-service');
    return null;
  }

  const handleServiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    const isServiceSelected = value !== 'no-service' && checked;

    // Set the selected service and update state accordingly
    setAlternativeUploadService(value);
    setSelectedServices(isServiceSelected ? [value] : []);

    // Send the selected service information
    send({ type: 'Select service', selectedService: isServiceSelected ? value : '' });

    // Dispatch data layer entry with the appropriate event action
    dispatchDataLayerEntry(
      'EXP1692 - Alternative upload file check PDP',
      'click',
      {
        eventCategory: 'product_detail_page',
        eventAction: isServiceSelected ? 'file_check_selected' : 'file_check_not_selected',
        eventNonInteraction: false,
      },
    );
  };

  return (
    <Panel id="delivery-tool-collapsible">
      <Panel.Header
        title={<FormattedMessage id="product.detail.file-check.title" defaultMessage="File check" />}
      >
        {alternativeUploadService === 'no-service' ? (
          <FormattedMessage
            id="product.detail.file-check.automatic"
            defaultMessage="Automatic file check"
          />
        ) : (
          <FormattedMessage
            id="product.detail.file-check.specialist"
            defaultMessage="File check by a specialist"
          />
        )}
      </Panel.Header>
      <div className="pd-flex pd-flex-col pd-gap-5">
        <RadioCardGroup
          id="alternative-upload-services"
          name="alternative-upload-services"
        >
          <RadioCardGroup.Fields className="pd-grid-cols-2">
            <RadioCardGroup.Field
              id="no-service"
              value="no-service"
              checked={alternativeUploadService === 'no-service'}
              onChange={handleServiceChange}
              label={<FormattedMessage id="product.detail.file-check.automatic" defaultMessage="Automatic file check" />}
              description={(
                <>
                  <p className="pd-mb-2">
                    <FormattedMessage id="checkout.payment.option.free" defaultMessage="Free" />
                  </p>
                  <p>
                    <FormattedMessage
                      id="product.detail.file-check.automatic.description"
                      defaultMessage="Our system automatically checks your print-ready PDF file for a number of relevant printing technical aspects"
                    />
                  </p>
                </>
              )}
              image={(
                <Icon
                  icon="check"
                  size="2xl"
                  className={clsx({
                    'pd-text-primary-500': alternativeUploadService === 'no-service',
                  })}
                />
              )}
            />
            <RadioCardGroup.Field
              id={fileCheckService.key}
              value={fileCheckService.key}
              checked={alternativeUploadService === fileCheckService.key}
              onChange={handleServiceChange}
              label={<FormattedMessage id="product.detail.file-check.specialist" defaultMessage="File check by a specialist" />}
              description={(
                <>
                  {fileCheckService.price && (
                  <p className="pd-mb-2 pd-whitespace-nowrap">
                    + <Money
                      value={fileCheckService.price[vatMode].centAmount}
                      currency={fileCheckService.price[vatMode].currencyCode}
                    />
                  </p>
                  )}
                  <p>
                    <FormattedMessage
                      id="product.detail.file-check.specialist.description"
                      defaultMessage="Our experienced colleagues check your file(s) for all relevant printing aspects"
                    />
                  </p>
                </>
              )}
              image={(
                <Icon
                  icon="eye-check"
                  size="2xl"
                  className={clsx({
                    'pd-text-primary-500': alternativeUploadService === fileCheckService.key,
                  })}
                />
              )}
            />
          </RadioCardGroup.Fields>
        </RadioCardGroup>
      </div>
    </Panel>
  );
};
